<div
  class="project-selection-dialog"
  [class.fullscreen]="isFullscreen"
  [class.compact]="!isFullscreen"
  [class.table-layout]="showTable"
>
  <div class="dialog-header">
    <h2 mat-dialog-title>Select Project</h2>
    <div class="header-actions">
      <app-account-menu
        [user$]="user$"
        (switchAccount)="onSwitchAccount()"
        (signOut)="onSignOut()"
        (navigateToBilling)="onNavigateToBilling()"
        (navigateToAdmin)="onNavigateToAdmin()"
      ></app-account-menu>
      @if (data.showCancel) {
        <button mat-icon-button type="button" (click)="onCancel()" aria-label="Close project selector">
          <mat-icon>close</mat-icon>
        </button>
      }
    </div>
  </div>

  <mat-dialog-content>
    @if (loading) {
      <div class="status-section">
        <mat-progress-bar mode="indeterminate" class="loading-progress"></mat-progress-bar>
        <p class="status-text">Loading your projects...</p>
      </div>
    }

    @if (error && !loading) {
      <div class="status-section error-section">
        <p class="error-message">{{ error }}</p>
        <button mat-button color="primary" type="button" (click)="retryLoad()">Retry</button>
      </div>
    }

    @if (!loading && !error) {
      @if (projects.length > 0) {
        <div class="selector-toolbar">
          <div class="search-field" role="search">
            <mat-icon class="search-icon" aria-hidden="true">search</mat-icon>
            <input
              [formControl]="searchControl"
              placeholder="Name, address, ID, or creator"
              aria-label="Search projects"
            />
            @if (searchControl.value) {
              <button
                mat-icon-button
                class="clear-search-button"
                type="button"
                (click)="clearSearch()"
                aria-label="Clear search"
              >
                <mat-icon>close</mat-icon>
              </button>
            }
          </div>

          @if (!showTable) {
            <button
              mat-stroked-button
              type="button"
              class="compact-filters-button"
              [class.active]="activeColumnFilterCount > 0"
              (click)="openFiltersDialog()"
              [attr.aria-label]="
                activeColumnFilterCount > 0 ? 'Filters (' + activeColumnFilterCount + ' active)' : 'Filters'
              "
            >
              <mat-icon>filter_list</mat-icon>
              <span>Filters</span>
              @if (activeColumnFilterCount > 0) {
                <span class="filter-badge">{{ activeColumnFilterCount }}</span>
              }
            </button>
          }

          @if (activeColumnFilterCount > 0) {
            <button
              mat-button
              type="button"
              class="clear-filters-button"
              (click)="clearAllColumnFilters()"
              aria-label="Clear all column filters"
            >
              <mat-icon>filter_alt_off</mat-icon>
              Clear filters
            </button>
          }

          @if (canToggleFullscreen || expanded) {
            <button
              mat-icon-button
              type="button"
              class="expand-toggle"
              (click)="toggleFullscreen()"
              [attr.aria-label]="fullscreenToggleLabel"
              [matTooltip]="fullscreenToggleHint"
              matTooltipPosition="above"
            >
              <mat-icon aria-hidden="true">{{ expanded ? 'close_fullscreen' : 'open_in_full' }}</mat-icon>
            </button>
          }
        </div>
      }

      @if (projects.length === 0) {
        <div class="status-section no-projects-section">
          <mat-icon class="no-projects-icon">folder_open</mat-icon>
          <p class="no-projects-text">Welcome! Let's get started with your first project.</p>
          <p class="no-projects-hint">Click "New Project" below to create your first construction project.</p>
        </div>
      } @else if (visibleProjects.length === 0) {
        <div class="status-section no-match-section">
          <mat-icon class="no-projects-icon">search_off</mat-icon>
          <p class="no-projects-text">{{ noMatchesText }}</p>
          <p class="no-projects-hint">{{ noMatchesHint }}</p>
          @if (activeColumnFilterCount > 0) {
            <button mat-stroked-button color="primary" type="button" (click)="clearAllColumnFilters()">
              Clear column filters
            </button>
          }
        </div>
      } @else if (showTable) {
        <div class="table-wrapper">
          <table
            mat-table
            [dataSource]="dataSource"
            matSort
            matSortActive="modified"
            matSortDirection="desc"
            class="projects-table"
          >
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>
                <div class="column-header-content">
                  <span>Name</span>
                  <app-column-filter
                    [config]="columnFilterConfigs['name']"
                    [criterion]="filterState['name']"
                    (criterionChange)="onColumnFilterChange('name', $event)"
                  ></app-column-filter>
                </div>
              </th>
              <td mat-cell *matCellDef="let project">
                <div class="primary-text" [title]="displayName(project)">{{ displayName(project) }}</div>
                <div class="secondary-text" [title]="project.architecturalProjectId">
                  {{ project.architecturalProjectId }}
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="address">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>
                <div class="column-header-content">
                  <span>Address</span>
                  <app-column-filter
                    [config]="columnFilterConfigs['address']"
                    [criterion]="filterState['address']"
                    (criterionChange)="onColumnFilterChange('address', $event)"
                  ></app-column-filter>
                </div>
              </th>
              <td mat-cell *matCellDef="let project" [title]="project.formattedAddress">
                {{ project.formattedAddress || '—' }}
              </td>
            </ng-container>

            <ng-container matColumnDef="ingested">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="numeric-header"
                matTooltip="Pages with a generated transcript, measured against the total extracted directly from uploaded PDFs"
              >
                <div class="column-header-content">
                  <span>Ingested pages</span>
                  <app-column-filter
                    [config]="columnFilterConfigs['ingested']"
                    [criterion]="filterState['ingested']"
                    (criterionChange)="onColumnFilterChange('ingested', $event)"
                  ></app-column-filter>
                </div>
              </th>
              <td mat-cell *matCellDef="let project" class="numeric-cell">
                <span
                  class="count-chip"
                  [class.count-unprocessed]="!isProcessed(project)"
                  [matTooltip]="progressHint(project)"
                  [attr.aria-label]="ingestedProgressLabel(project)"
                >
                  <span class="count-value">{{ countDisplay(project, project.ingestedPagesCount) }}</span>
                  @if (isProcessed(project)) {
                    <span class="count-total">of {{ uploadedPagesDisplay(project) }}</span>
                  }
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="analyzed">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="numeric-header"
                matTooltip="Pages with at least one code applicability report, measured against ingested pages"
              >
                <div class="column-header-content">
                  <span>Analyzed pages</span>
                  <app-column-filter
                    [config]="columnFilterConfigs['analyzed']"
                    [criterion]="filterState['analyzed']"
                    (criterionChange)="onColumnFilterChange('analyzed', $event)"
                  ></app-column-filter>
                </div>
              </th>
              <td mat-cell *matCellDef="let project" class="numeric-cell">
                <span
                  class="count-chip"
                  [class.count-unprocessed]="!isProcessed(project)"
                  [matTooltip]="progressHint(project)"
                  [attr.aria-label]="analyzedProgressLabel(project)"
                >
                  <span class="count-value">{{ countDisplay(project, project.analyzedPagesCount) }}</span>
                  @if (isProcessed(project)) {
                    <span class="count-total">of {{ countValue(project.ingestedPagesCount) }}</span>
                  }
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="compliance">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="numeric-header"
                matTooltip="Distinct ICC code sections evaluated. A section evaluated on ten pages counts once"
              >
                <div class="column-header-content">
                  <span>Compliance sections</span>
                  <app-column-filter
                    [config]="columnFilterConfigs['compliance']"
                    [criterion]="filterState['compliance']"
                    (criterionChange)="onColumnFilterChange('compliance', $event)"
                  ></app-column-filter>
                </div>
              </th>
              <td mat-cell *matCellDef="let project" class="numeric-cell">
                <span
                  class="count-chip"
                  [class.count-unprocessed]="!isProcessed(project)"
                  [matTooltip]="progressHint(project)"
                  [attr.aria-label]="countLabel(project, project.complianceSectionsCount, 'Compliance sections')"
                >
                  <span class="count-value">{{ countDisplay(project, project.complianceSectionsCount) }}</span>
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="created">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>
                <div class="column-header-content">
                  <span>Created</span>
                  <app-column-filter
                    [config]="columnFilterConfigs['created']"
                    [criterion]="filterState['created']"
                    (criterionChange)="onColumnFilterChange('created', $event)"
                  ></app-column-filter>
                </div>
              </th>
              <td mat-cell *matCellDef="let project">
                {{ project.createdAt ? (project.createdAt | date: 'MMM d, y') : '—' }}
              </td>
            </ng-container>

            <ng-container matColumnDef="modified">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>
                <div class="column-header-content">
                  <span>Last modified</span>
                  <app-column-filter
                    [config]="columnFilterConfigs['modified']"
                    [criterion]="filterState['modified']"
                    (criterionChange)="onColumnFilterChange('modified', $event)"
                  ></app-column-filter>
                </div>
              </th>
              <td mat-cell *matCellDef="let project">
                {{ lastModified(project) ? (lastModified(project) | date: 'MMM d, y') : '—' }}
              </td>
            </ng-container>

            <ng-container matColumnDef="creator">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>
                <div class="column-header-content">
                  <span>Created by</span>
                  <app-column-filter
                    [config]="columnFilterConfigs['creator']"
                    [criterion]="filterState['creator']"
                    (criterionChange)="onColumnFilterChange('creator', $event)"
                  ></app-column-filter>
                </div>
              </th>
              <td mat-cell *matCellDef="let project" [title]="project.createdBy">
                {{ project.createdBy || '—' }}
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr
              mat-row
              *matRowDef="let project; columns: displayedColumns"
              class="project-table-row"
              tabindex="0"
              role="button"
              data-testid="project-selector-row"
              [attr.data-project-id]="project.architecturalProjectId"
              [attr.data-project-name]="displayName(project)"
              [attr.data-has-progress]="project.hasProgressData === true"
              [attr.data-ingested-pages]="project.ingestedPagesCount ?? ''"
              [attr.data-uploaded-pages]="project.uploadedPagesCount ?? ''"
              [attr.data-analyzed-pages]="project.analyzedPagesCount ?? ''"
              [attr.data-compliance-sections]="project.complianceSectionsCount ?? ''"
              [attr.aria-label]="'Open project ' + displayName(project)"
              (click)="selectProject(project)"
              (keydown)="onRowKeydown($event, project)"
            ></tr>
          </table>
        </div>
      } @else {
        <div class="projects-list">
          @for (project of visibleProjects; track project.architecturalProjectId) {
            <button
              type="button"
              class="project-row"
              data-testid="project-selector-row"
              [attr.data-project-id]="project.architecturalProjectId"
              [attr.data-project-name]="displayName(project)"
              [attr.data-has-progress]="project.hasProgressData === true"
              [attr.data-ingested-pages]="project.ingestedPagesCount ?? ''"
              [attr.data-uploaded-pages]="project.uploadedPagesCount ?? ''"
              [attr.data-analyzed-pages]="project.analyzedPagesCount ?? ''"
              [attr.data-compliance-sections]="project.complianceSectionsCount ?? ''"
              [attr.aria-label]="'Open project ' + displayName(project)"
              (click)="selectProject(project)"
            >
              <mat-icon class="project-icon" aria-hidden="true">folder</mat-icon>
              <div class="project-content">
                <div class="primary-text">{{ displayName(project) }}</div>
                <div class="secondary-text">
                  {{ project.formattedAddress || project.architecturalProjectId }}
                </div>
                <div
                  class="progress-text"
                  [class.count-unprocessed]="!isProcessed(project)"
                  [attr.aria-label]="'Processing progress: ' + progressLine(project)"
                >
                  {{ progressLine(project) }}
                </div>
                <div class="meta-text">
                  @if (lastModified(project)) {
                    <span>Updated {{ lastModified(project) | date: 'MMM d, y' }}</span>
                  }
                  @if (project.createdAt) {
                    <span>Created {{ project.createdAt | date: 'MMM d, y' }}</span>
                  }
                  @if (project.createdBy) {
                    <span>{{ project.createdBy }}</span>
                  }
                </div>
              </div>
            </button>
          }
        </div>
      }
    }
  </mat-dialog-content>

  <mat-dialog-actions class="dialog-actions">
    <span [matTooltip]="newProjectTooltip" matTooltipPosition="above">
      <button
        mat-button
        type="button"
        (click)="onNewProject()"
        class="new-project-button"
        [disabled]="!canCreateNewProject"
      >
        <mat-icon>add</mat-icon>
        New Project
      </button>
    </span>
    @if (!loading && !error) {
      <p class="result-count" role="status" aria-live="polite" aria-atomic="true">
        @if (hasActiveFilter) {
          {{ visibleProjects.length }} of {{ projects.length }} {{ projects.length === 1 ? 'project' : 'projects' }}
        } @else {
          {{ projects.length }} {{ projects.length === 1 ? 'project' : 'projects' }}
        }
      </p>
    } @else {
      <span class="action-spacer" aria-hidden="true"></span>
    }
    @if (data.showCancel) {
      <button mat-button type="button" (click)="onCancel()">Cancel</button>
    }
  </mat-dialog-actions>
</div>
